<template>
  <div
    ref="tableRef"
    :class="rtTableClass"
    class="rt-table"
  >
    <div
      v-if="hasHeader"
      ref="tableHeaderRef"
      :style="headerStyle"
      class="rt-table-header"
    >
      <slot name="header"></slot>
      <div class="rt-table-header__left">
        <slot name="header-left">
        </slot>
      </div>
      <div class="rt-table-header__right">
        <slot name="header-right">
        </slot>
      </div>
      <div class="rt-table-header__center">
        <slot name="header-center">
        </slot>
      </div>
    </div>
    <div
      ref="tableContentRef"
      :class="['is-' + size]"
      class="rt-table-content"
    >
      <el-table
        v-if="autoHeight"
        ref="table"
        v-bind="$attrs"
        :stripe="stripe"
        :data="dataList"
        :row-key="rowKey"
        :row-class-name="rowClassNameFn"
        :cell-class-name="cellClassNameFn"
        :header-row-class-name="headerCls"
        :border="showBorder"
        :class="['rt-table-item', { 'rt-table-draggable': draggable }]"
        :header-sticky="isSticky"
        :loading="loading"
        :show-fix-columns="showFixColumns"
        :radio="radio"
        :empty-text="emptyText"
        :total-record="totalRecord"
        :mark-hover-column="markHoverColumn"
        :mark-sorted-column="markSortedColumn"
        :empty-cell="emptyCell"
        :optimize-fixed="optimizeFixed"
        v-on="$listeners"
        @row-click="handleClickTableRow"
        @row-dblclick="rowDblclickHandler"
        @selection-change="selectItem"
        @cell-edit-dblclick="handleCellClick"
      >
        <template>
          <el-table-column
            v-if="computedSelected"
            type="selection"
            :reserve-selection="computedKeepSelected"
            width="48"
            @selection-change="handleSelectionChange"
          />
          <el-table-column
            v-if="draggable"
            width="48"
          >
            <template slot-scope="scope">
              <div
                :class="
                  disableDragFunc && disableDragFunc(scope.row)
                    ? 'rt-move rt-not-move'
                    : 'rt-move'
                "
                @click="e => dragBtnClick(e, scope.row)"
              >
                <rt-icon
                  name="rt-base-drag"
                  size="24"
                />
              </div>
            </template>
          </el-table-column>
          <slot />
        </template>
        <slot
          slot="empty"
          name="empty"
        />
      </el-table>
      <el-table
        v-else
        ref="table"
        v-bind="$attrs"
        :stripe="stripe"
        :data="dataList"
        :row-key="rowKey"
        :row-class-name="rowClassNameFn"
        :cell-class-name="cellClassNameFn"
        :header-row-class-name="headerCls"
        :border="showBorder"
        :header-sticky="isSticky"
        :show-fix-columns="showFixColumns"
        :radio="radio"
        :empty-text="emptyText"
        :total-record="totalRecord"
        :mark-hover-column="markHoverColumn"
        :mark-sorted-column="markSortedColumn"
        :empty-cell="emptyCell"
        :optimize-fixed="optimizeFixed"
        class="rt-table-item"
        v-on="$listeners"
        @row-click="handleClickTableRow"
        @row-dblclick="rowDblclickHandler"
        @selection-change="selectItem"
        @cell-edit-dblclick="handleCellClick"
      >
        <template>
          <el-table-column
            v-if="computedSelected"
            type="selection"
            :reserve-selection="computedKeepSelected"
            width="48"
            @selection-change="handleSelectionChange"
          />
          <el-table-column
            v-if="draggable"
            width="48"
          >
            <template slot-scope="scope">
              <div
                :class="
                  disableDragFunc && disableDragFunc(scope.row)
                    ? 'rt-move rt-not-move'
                    : 'rt-move'
                "
                @click="e => dragBtnClick(e, scope.row)"
              >
                <rt-icon
                  name="rt-base-drag"
                  size="24"
                />
              </div>
            </template>
          </el-table-column>
          <slot />
        </template>
        <slot
          slot="empty"
          name="empty"
        />
      </el-table>

      <div
        v-if="hasFooter"
        ref="tableFooter"
        :style="footerStyle"
        :rt-xpc-sticky-bottom="isSticky"
        rt-xpc-sticky-group="rtTableFooter"
        class="rt-table-footer"
      >
        <div class="rt-table-pagination-scroll">
          <div class="rt-table-pagination-scroll-content">
            <div class="rt-table-pagination-scroll-bar"></div>
          </div>
        </div>

        <div class="rt-table-footer__left">
          <slot name="footer-left">
            <div
              v-if="computedShowSelected"
              class="rt-table-selected"
            >
              <span class="rt-table-selected-text">
                {{ $tx('已选', 'xpc.table.selectedItemsPrev') }}
                <span class="rt-table-selected-number">{{
                  selection.length
                }}</span>
                {{ $tx('项', 'xpc.table.selectedItemsNext') }}
              </span>
              <rt-icon
                v-if="selection.length"
                class="rt-table-selected-close"
                type="iconFont"
                :size="14"
                name="rt-base-close-x-lined"
                @click="confirmClearSelection"
              ></rt-icon>
            </div>
          </slot>
        </div>
        <div class="rt-table-footer__right">
          <slot name="footer-right">
            <div
              v-if="computedShowPagination"
              class="rt-table-pagination"
            >
              <rt-pagination
                :current-page="pageIndex"
                :is-table-click="true"
                :page-size="pageSizeTable"
                :page-sizes="pageSizes"
                :pager-count="pagerCount"
                :layout="pageLayout"
                :total="totalRecord"
                :background="computedPageBackground"
                :small="small"
                @current-change="handleIndexChange"
                @size-change="handleSizeChange"
                prevText="pre page"
                nextText="next page"
              >
              </rt-pagination>
            </div>
          </slot>
        </div>
        <div class="rt-table-footer__center">
          <slot name="footer-center">
          </slot>
        </div>
      </div>
    </div>
    <RTtableCellEditor
      v-if="editDisplay"
      ref="editCell"
      :key="editCellId"
      class="rt-table-cell-editor"
      :v-node="editTemplate"
      :prop="editProp"
      :row="editRow"
      :rules="_rules"
      :need-immediate-validate="needImmediateValidate"
      @edit-update="handleUpdate"
      @edit-esc="handleEditEsc"
      @validate-error="$emit('validate-error')"
      @hide="hideEditCell"
    ></RTtableCellEditor>
  </div>
</template>

<script>
import { store } from './store';
import ElTable from 'vue-xpc/src/element-ui/packages/table';
import ElTableColumn from 'vue-xpc/src/element-ui/packages/table/src/table-column';
import { stickyItem } from 'vue-xpc/src/mixins/sticky';
import {
  scrollIntoView,
  closest,
  getSessionStorage,
  setSessionStorage,
  clearSessionStorage,
  isMac
} from 'vue-xpc/src/utils/util.js';
import RTtableCellEditor from './rtTableCellEditor.vue';

const methods = [
  'clearSelection',
  'toggleRowSelection',
  'toggleMultiRowSelection',
  'toggleAllSelection',
  'toggleRowExpansion',
  'setCurrentRow',
  'clearSort',
  'clearFilter',
  'doLayout',
  'sort'
];

export default {
  name: 'RtTable',
  components: { ElTable, ElTableColumn, RTtableCellEditor },
  mixins: [stickyItem],
  props: {
    /**
     * @public 选中行的背景色
     */
    selectedBgColor: {
      type: String,
      default: 'warning'
    },
    size: {
      type: String,
      default: 'small'
    },
    small: {
      type: Boolean,
      default: true
    },
    /**
     * @public
     * 是否显示分页器
     */
    showPagination: {
      type: Boolean,
      default: true
    },
    /**
     * @public
     * 是否显示边框
     */
    showBorder: {
      type: Boolean,
      default: true
    },
    /**
     * @public
     * 是否支持拖拽
     */
    draggable: {
      type: Boolean,
      default: false
    },
    totalRecord: {
      type: Number,
      default: 0
    },
    fetch: {
      type: Function,
      required: true
    },
    autoHeight: {
      type: Boolean,
      default: true
    },
    sectionable: {
      type: Boolean,
      default: true
    },
    radio: {
      type: Boolean,
      default: false
    },
    stripe: {
      type: Boolean,
      default: false
    },
    pageLayout: {
      type: String,
      default: 'total, sizes, prev, pager, next, jumper'
    },
    // 表格分页默认不加背景色
    pageBackground: {
      type: Boolean,
      default: false
    },
    pagerCount: Number,
    pageSize: {
      type: Number,
      default: 20
    },
    defaultPageIndex: {
      type: Number,
      default: 1
    },
    listPage: {
      type: Boolean,
      default: false
    },
    sticky: {
      type: Boolean,
      default: false
    },
    // header部分的字重，可选值  primary/medium/bold
    headerWeight: {
      type: String,
      default: 'primary'
    },
    // footer 内间距, 【normal-默认、small、large、自定义数值】
    footerPadding: {
      type: [String, Number],
      default: 'small'
    },
    // header 内间距, 【normal-默认、small、large、自定义数值】
    headerPadding: {
      type: [String, Number],
      default: 'normal'
    },
    rowKey: [String, Function],
    // 是否允许行点击选中
    enableRowClick: {
      type: Function,
      default: (row, column, event) => {
        const BOUNDARY_TAGS = ['TR', 'TD', 'TH'];
        const BOUNDARY_CLASSES = ['cell'];
        const INTERACTIVE_TAGS = ['INPUT', 'TEXTAREA', 'BUTTON'];
        const INTERACTIVE_CLASSES = [
          'rt-time-picker',
          'rt-date-picker',
          'rt-input',
          'rt-picklist',
          'rt-radio-group',
          'rt-radio',
          'rt-checkbox-group',
          'rt-checkbox',
          'rt-lookup',
          'rt-input-number',
          'rt-switch'
        ];

        /**
         * 当前点击组件是否为交互型组件
         */
        const isInteractiveComponent = node => {
          // 是否tagName在范围内
          if (INTERACTIVE_TAGS.includes(node.tagName.toUpperCase())) {
            return true;
          }

          // 是否className在范围内
          if (INTERACTIVE_CLASSES.some(cls => node.classList.contains(cls))) {
            return true;
          }

          // 如果父节点已遍历到cell或td、tr、th，则停止查找
          const parentNode = node.parentNode;
          if (
            parentNode ||
            BOUNDARY_TAGS.includes(parentNode.tagName.toUpperCase()) ||
            BOUNDARY_CLASSES.some(cls => parentNode.classList.contains(cls))
          ) {
            return false;
          }

          return isInteractiveComponent(parentNode);
        };

        if (event && event.target) {
          return !isInteractiveComponent(event.target);
        }

        return true;
      }
    },
    /**
     * @public 是否在数据更新之后保留之前选中的数据
     */
    keepSelected: {
      type: Boolean,
      default: true
    },
    // 支持最后一次阅读
    autoLastVisitedRow: {
      type: Boolean,
      default: false
    },
    lastVisitedBgColor: {
      type: String,
      default: 'primary'
    },
    rowClassName: {
      type: [Function, String]
    },
    cellClassName: {
      type: [Function, String]
    },
    rules: {
      type: Object,
      default: null
    },
    editPrefix: {
      type: String,
      default: '_edit_'
    },
    optimizeFixed: {
      type: [Boolean, Number],
      default: 20
    },
    emptyText: {
      type: String
    },
    // fetchData后滚动条自动滚动
    autoScroll: {
      type: Boolean,
      default: true
    },
    // 标记悬表头浮列
    markHoverColumn: {
      type: Boolean,
      default: false
    },
    // 标记排序后的列
    markSortedColumn: {
      type: Boolean,
      default: false
    },
    // 展示已选项
    showSelected: {
      type: Boolean,
      default: true
    },
    emptyCell: {
      type: String,
      default: '--'
    }
  },
  provide () {
    return {
      xpcPaginationContainer: this
    };
  },
  data () {
    return {
      pageIndex: this.defaultPageIndex,
      dataList: [],
      pageSizes: [5, 10, 20, 30, 40, 50],
      state: store.state,
      loading: false,
      pageSizeTable: this.pageSize,
      scrollTop: 0,
      body: null,
      multipleSelection: [], // 表格被选中的内容
      isSticky: false,
      lastVisitedRow: undefined,
      showFixColumns: false,

      editTarget: null,
      editTargetResizeOb: null,
      editTemplate: null,
      editPosition: { width: 0, height: 0, x: 0, y: 0 },
      editDisplay: false,
      editProp: '',
      editRow: {},
      editCellId: '',
      editType: '',

      selection: [],
      // 可编辑单元格是否需要立即校验
      needImmediateValidate: false
    };
  },
  computed: {
    // 定义 header 内间距
    headerStyle () {
      if (this.headerPadding === 'normal') {
        return { padding: this.SCSS_VARIABLES.DOUBLE_GAP };
      }
      if (this.headerPadding === 'small') {
        return { padding: this.SCSS_VARIABLES.GAP };
      }
      if (this.headerPadding === 'large') {
        return { padding: this.SCSS_VARIABLES.TRIPLE_GAP };
      } else {
        let padding = (this.headerPadding + '').endsWith('px')
          ? this.headerPadding
          : this.headerPadding + 'px';
        return { padding: padding };
      }
    },
    // 定义 footer 内间距
    footerStyle () {
      const style = {};
      if (this.footerPadding === 'normal') {
        style.padding = `${this.SCSS_VARIABLES.DOUBLE_GAP} ${this.SCSS_VARIABLES.TRIPLE_GAP}`;
      } else if (this.footerPadding === 'small') {
        style.padding = `${this.SCSS_VARIABLES.GAP} ${this.SCSS_VARIABLES.TRIPLE_GAP}`;
      } else if (this.footerPadding === 'large') {
        style.padding = `${this.SCSS_VARIABLES.TRIPLE_GAP} ${this.SCSS_VARIABLES.TRIPLE_GAP}`;
      } else {
        let padding = (this.footerPadding + '').endsWith('px')
          ? this.footerPadding
          : this.footerPadding + 'px';
        style.padding = `${padding}  ${this.SCSS_VARIABLES.TRIPLE_GAP}`;
      }

      if (!this.computedShowSelected && !this.computedShowPagination) {
        style.padding = 0;
      }

      return style;
    },
    // 处理方式待定 - 分页设计稿确认再处理，
    computedPageBackground () {
      if (this.small) return false;
      else return this.pageBackground;
    },
    hasHeader () {
      return (
        !!this.$slots.header ||
        this.$slots['header-left'] ||
        this.$slots['header-center'] ||
        this.$slots['header-right']
      );
    },
    hasFooter () {
      return (
        this.computedShowPagination ||
        this.computedShowSelected ||
        !!this.$slots['footer-left'] ||
        this.$slots['footer-center'] ||
        this.$slots['footer-right']
      );
    },
    total () {
      return this.$tx(
        `共 ${this.totalRecord} 条记录`,
        'xpc.common.totalRecords',
        { count: this.totalRecord }
      );
    },
    headerCls () {
      return [`header-weight--${this.headerWeight}`].join(' ');
    },
    // 验证row-key
    computedKeepSelected () {
      return !!(this.keepSelected && this.rowKey);
    },
    computedSelected () {
      return this.sectionable;
    },
    _rules () {
      let obj = {};
      if (this.rules) {
        Object.keys(this.rules).forEach(item => {
          obj[this.editPrefix + item] = this.rules[item];
        });
      }
      return obj;
    },
    rtTableClass () {
      const className = ['rt-table'];

      if (this.showBorder) {
        className.push('rt-table--border');
      }

      if (!this.hasFooter) {
        className.push('rt-table--no-footer');
      }

      if (!this.computedShowPagination) {
        className.push('rt-table--no-pagination');
      }

      if (this.radio) {
        className.push('is-radio');
      }

      if (this.isSticky) {
        className.push('is-sticky');
      }

      if (this.markHoverColumn || this.markSortedColumn) {
        className.push('has-mark-table-header');
      }

      if (this.dataList.length === 0) {
        className.push('is-empty');
      }

      if (isMac()) {
        className.push('is-mac');
      }

      return className;
    },
    computedShowPagination () {
      return this.showPagination && this.totalRecord > 5;
    },
    computedShowSelected () {
      return (
        this.showSelected &&
        !this.radio &&
        this.totalRecord > 0 &&
        this.computedSelected
      );
    }
  },
  watch: {
    editTarget (val, oldVal) {
      if (typeof ResizeObserver === 'function') {
        if (this.editTargetResizeOb) {
          this.editTargetResizeOb.unobserve(oldVal);
        }

        this.editTargetResizeOb = new ResizeObserver(() => {
          if (val) {
            const { width, height } = val.getBoundingClientRect();
            this.editPosition.width = width;
            this.editPosition.height = height;
          }
        });

        this.editTargetResizeOb.observe(val);
      }
    }
  },
  mounted () {
    if (typeof ResizeObserver === 'function') {
      const resizeOb = new ResizeObserver(() => {
        this.$refs.table && this.$refs.table.doLayout();
        this.appBodyScrollHandler();
      });
      let tableBody;
      if (this.$refs.table.$el) {
        const tableWrapper = this.$refs.table.$el.querySelector(
          '.el-table__body-wrapper'
        );
        tableBody = tableWrapper.querySelector('table.el-table__body');
        //
        if (tableBody) {
          resizeOb.observe(tableBody);
        }
      }
      this.$once('hook:beforeDestroy', () => {
        resizeOb.unobserve(tableBody);
        this.removeHorizontalListener();
      });
    }

    this.loadData();
    this.$nextTick(() => {
      // 将 elementui table 组件原生方法附加在 rtTable 上面
      methods.forEach(method => {
        this[method] = this.$refs.table[method];
      });
      this.getLastVisitedRowByStorage();

      // 适配mac和window，因为ele在table最后添加了一个gutter，在window上会显示
      // css选择器无法选取指定class名的最后一个元素，故而使用js添加一个特殊className操作
      let columns = this.$el.getElementsByClassName('is-leaf');

      if (columns.length > 0) {
        let tagColumn = columns[columns.length - 1];
        tagColumn.classList.add('the-last-one');
      }
      this.scrollTop = 0;
      if (this.rtXpcStickyContainer) {
        this.appBody = this.rtXpcStickyContainer.getContainer();
      } else {
        this.appBody =
          closest(this.$refs.tableRef, '[rt-xpc-sticky-container]') ||
          document.querySelector('[rt-xpc-sticky-container]') ||
          document.querySelector('.qiankun-content') ||
          document.querySelector('.app-body');
      }

      this.appBodyScrollHandler();
      if (
        closest(this.$refs.tableRef, `[rt-xpc-sticky-container]`) &&
        (this.sticky || this.listPage) &&
        !closest(this.$refs.tableRef, '.el-dialog__wrapper')
      ) {
        this.isSticky = true;
      }
      if (this.isSticky) {
        this.appBody &&
          this.appBody.addEventListener('scroll', this.appBodyScrollHandler);
      } else {
        this.showFixColumns = true;
      }
    });
    this.lastVisitedKey = this.getTableStorageKey('last-visited');
    window.addEventListener('beforeunload', this.clearLastVisitedRow);
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.clearLastVisitedRow);
    if (this.isSticky) {
      this.appBody &&
        this.appBody.removeEventListener('scroll', this.appBodyScrollHandler);
    }
    if (this.editTargetResizeOb) {
      this.editTargetResizeOb.unobserve(this.editTarget);
    }
    if (this.isSticky) {
      this.appBody &&
        this.appBody.removeEventListener('scroll', this.appBodyScrollHandler);
    }
  },

  methods: {
    /**
     * 清空已选项
     */
    clearSelection () {
      this.selection = [];
      this.$refs.table.clearSelection();
    },
    confirmClearSelection () {
      this.$confirm(
        this.$tx('确定移除所有选中项？', 'xpc.table.deleteAllSelected'),
        this.$tx('确认提示', 'xpc.table.confirmationPrompt'),
        {
          confirmButtonText: this.$tx('确定', 'xpc.common.ok'),
          cancelButtonText: this.$tx('取消', 'xpc.common.cancel'),
          type: 'warning'
        }
      )
        .then(() => {
          this.clearSelection();
          this.$emit('clear-selection');
        })
        .catch(() => {});
    },
    /**
     * 选中项变化时，派发事件传递所有选中项
     * @param {Array} rows 所有选中项
     */
    selectItem (rows) {
      this.selection = rows;
      this.$emit('select-item', rows);
    },
    handleClickTableRow (row, column, event) {
      if (
        !this.$xpc.isFunction(this.enableRowClick) ||
        this.enableRowClick(row, column, event) === false
      ) {
        return;
      }
      if (this.computedSelected) {
        this.$refs.table.toggleRowSelection(row);
      }
    },
    handleSelectionChange (rows) {
      this.multipleSelection = rows;
    },
    loadData () {
      this.pageIndex = this.defaultPageIndex;
      // 初始表格数据，判断是否为表格内部分页
      this.fetchData();
    },
    fetchData () {
      let pageIndex, pageSize;
      for (var key in this.$slots) {
        if (key !== 'header' && key !== 'default') {
          const slot = this.$slots[key];
          const params =
            slot &&
            slot[0] &&
            slot[0].child &&
            slot[0].child.getParams &&
            slot[0].child.getParams();
          if (params) {
            pageIndex = params.pageIndex;
            pageSize = params.pageSize;
          }
        }
      }
      this.loading = true;
      this.fetch({
        pageIndex: this.showPagination ? this.pageIndex : pageIndex,
        pageSize: this.showPagination ? this.pageSizeTable : pageSize
      })
        .then(resp => {
          // 处理返回结果
          this.dataList = resp || [];
          // this.dataList = resp.slice(0,4) || [];
          // 通过总数量与pageSize比较来判断是否显示分页
          this.morePages = this.totalRecord > this.pageSizeTable;
          if (this.dataList.length === 0 && this.pageIndex > 1) {
            this.pageIndex = Math.ceil(this.totalRecord / this.pageSizeTable);
            this.fetchData();
            return Promise.reject(new Error());
          }
          this.$nextTick(() => {
            this.appBodyScrollHandler();
            if (this.isSticky && this.autoScroll) {
              if (
                window.rtXpcStickyContainer &&
                window.rtXpcStickyContainer.scrollStickyItem
              ) {
                window.rtXpcStickyContainer.scrollStickyItem(
                  this.$refs.tableRef
                );
              } else if (
                this.rtXpcStickyContainer &&
                this.rtXpcStickyContainer.scrollStickyItem
              ) {
                this.rtXpcStickyContainer.scrollStickyItem(this.$refs.tableRef);
              }
            }
          });
        })
        .then(() => {
          if (this.autoHeight) {
            this.$nextTick(() => {
              this.updateHeight();
              this.loading = false;
              this.$emit('load-complete');
            });
          } else {
            this.loading = false;
            this.$emit('load-complete');
          }
        })
        .finally(() => {
          this.$setTimeout(() => {
            this.hideEditCell();
            if (this.$refs.table) {
              this.$refs.table.updateFixColumnsReact();
            }
            this.showFixColumns = true;
          }, 100);
        });
    },
    handleSizeChange (val) {
      this.pageSizeTable = val;
      this.pageIndex = 1;
      this.$emit('changesize', val);
      // this.fetchData();
    },
    handleIndexChange (val) {
      this.pageIndex = val;
      this.$emit('changeindex', val);
      // this.fetchData();
    },
    updateHeight () {
      store.UPDATE_TABLE_HEIGHT();
    },
    /**
     * @deprecated
     * 方法已废弃
     */
    computedScroll () {
      // 列表页滑动
      // 如果滑动就固定在底部
      this.$xpc.deprecatedLog(
        'rtTable',
        'computedScroll',
        'appBodyScrollHandler',
        'fn'
      );
    },
    /**
     * @deprecated
     * 方法已废弃
     */
    handleScroll () {
      this.$xpc.deprecatedLog(
        'rtTable',
        'handleScroll',
        'appBodyScrollHandler',
        'fn'
      );
    },
    /**
     * appBody 滚动处理
     */
    appBodyScrollHandler () {
      if (this.isSticky) {
        this.controlStickyTablePagination();
        this.addHorizontalListener();
      }
    },
    /**
     * 控制吸底的分页器显隐
     */
    controlStickyTablePagination () {
      const tableFooter =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.rt-table-footer');
      const tableContent = this.$refs.tableContentRef;
      if (!tableFooter || !tableContent) {
        return;
      }

      const tableScroll = this.$refs.tableRef.querySelector(
        '.el-table__body-wrapper'
      );
      const tableBody = tableScroll.querySelector('table');
      const offsetWidth = Math.max(
        tableScroll.getBoundingClientRect().width,
        tableBody.getBoundingClientRect().width
      );
      const paginationScroll = tableFooter.querySelector(
        '.rt-table-pagination-scroll'
      );
      const paginationScrollBar = tableFooter.querySelector(
        '.rt-table-pagination-scroll-bar'
      );
      // 说明不应该有滚动条
      if (tableScroll.getAttribute('class').includes('is-scrolling-none')) {
        paginationScroll.style.display = 'none';
        paginationScrollBar.style.width = '100%';
      } else {
        paginationScroll.style.display = 'unset';
        paginationScrollBar.style.width = Math.floor(offsetWidth) + 'px';
      }

      // 给吸底的滚动条设置与表格相同的宽度
    },
    /**
     * @private 分页器滚动条的监听函数
     */
    paginationScrollListener () {
      const tableFooter =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.rt-table-footer');
      const tableScroll =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.el-table__body-wrapper');
      if (!tableFooter) {
        return;
      }
      const paginationScroll = tableFooter.querySelector(
        '.rt-table-pagination-scroll-content'
      );
      tableScroll.scrollLeft = paginationScroll.scrollLeft;
    },
    /**
     * @private 表格横向滚动监听函数
     */
    tableScrollListener () {
      const tableFooter =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.rt-table-footer');

      const tableScroll =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.el-table__body-wrapper');
      if (!tableFooter) {
        return;
      }
      const paginationScroll = tableFooter.querySelector(
        '.rt-table-pagination-scroll-content'
      );

      paginationScroll.scrollLeft = tableScroll.scrollLeft;
    },
    /**
     * @private 添加横向滚动的事件
     */
    addHorizontalListener () {
      const tableFooter =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.rt-table-footer');

      const tableScroll =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.el-table__body-wrapper');
      if (!tableFooter) {
        return;
      }
      const paginationScroll = tableFooter.querySelector(
        '.rt-table-pagination-scroll-content'
      );

      paginationScroll.addEventListener(
        'scroll',
        this.paginationScrollListener
      );
      tableScroll.addEventListener('scroll', this.tableScrollListener);
    },
    /**
     * @private 移除横向滚动的监听事件
     */
    removeHorizontalListener () {
      const tableFooter =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.rt-table-footer');
      const tableScroll =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.el-table__body-wrapper');
      if (!tableFooter) {
        return;
      }
      const paginationScroll = tableFooter.querySelector(
        '.rt-table-pagination-scroll-content'
      );

      paginationScroll &&
        paginationScroll.removeEventListener(
          'scroll',
          this.paginationScrollListener
        );
      tableScroll &&
        tableScroll.removeEventListener('scroll', this.tableScrollListener);
    },
    dragBtnClick () {
      console.log('未安装拖拽插件');
    },
    rowClassNameFn (row, rowIndex) {
      let className = `selected-bg-color-${this.selectedBgColor} `;
      if (this.rowClassName) {
        if (typeof this.rowClassName === 'function') {
          const cn = this.rowClassName(row.row, rowIndex);
          className += `${cn} `;
        } else {
          className += `${this.rowClassName} `;
        }
      }

      return className;
    },
    cellClassNameFn ({ row, column, rowIndex, columnIndex }) {
      let className = ``;
      if (this.cellClassName) {
        if (typeof this.cellClassName === 'function') {
          const cn = this.cellClassName(row, rowIndex);
          className += `${cn} `;
        } else {
          className += `${this.cellClassName} `;
        }
      }

      if (this.lastVisitedRow && this.equalsRow(row, this.lastVisitedRow)) {
        className += `rt-xpc-bg-${this.lastVisitedBgColor}-light `;
      }

      return className;
    },
    rowDblclickHandler (row, column, event) {
      if (this.autoLastVisitedRow) {
        this.addLastVisitedRow(row);
      }
    },
    addLastVisitedRow (d) {
      this.setLastVisitedRowByStorage(d);
      this.lastVisitedRow = d;
    },
    equalsRow (row1, row2) {
      if (this.rowKey) {
        if (typeof this.rowKey !== 'function') {
          return row1[this.rowKey] === row2[this.rowKey];
        } else {
          return this.rowKey(row1) === this.rowKey(row2);
        }
      } else {
        return row1 === row2;
      }
    },
    getTableStorageKey (suffix) {
      let path = '';
      let key = '';
      if (this.$route) {
        path = this.$route.path;
      } else {
        path = location.href;
      }
      const nodes = document.querySelectorAll('.rt-table');
      if (nodes && nodes.length > 0) {
        nodes.forEach((node, index) => {
          if (node === this.$refs.tableRef) {
            key = `${path}-rt-table-${index}-${suffix}`;
          }
        });
      }

      return key;
    },
    getLastVisitedRowByStorage () {
      if (this.lastVisitedKey) {
        const row = getSessionStorage(this.lastVisitedKey);
        if (row) {
          this.addLastVisitedRow(row);
        }
      }
    },
    setLastVisitedRowByStorage (row) {
      if (this.lastVisitedKey) {
        setSessionStorage(this.lastVisitedKey, row);
      }
    },
    clearLastVisitedRow () {
      this.lastVisitedRow = undefined;
      clearSessionStorage(this.lastVisitedKey);
    },

    handleCellClick (row, column, target) {
      // 判断是否为可编辑单元格
      if (!column.renderEditCell || !column.renderEditCell({ row })) return;
      // 如果可编辑框已存在，且存在校验失败的情况，不允许点击
      const cellKeyValue = this.getRowKeyValue(row);
      if (
        this.editDisplay &&
        !!this.$refs.editCell.$el.querySelector('.is-error')
      ) {
        const errorCell = this.$refs.editCell.$el.getAttribute(
          'data-edit-cell-key'
        );
        scrollIntoView(
          this.$refs.tableRef.querySelector(`.rt-edit-cell-${errorCell}`)
        );
        return;
      }
      // 临时挂载可编辑单元格数据
      row[this.editPrefix + column.property] =
        row[column.property] &&
        JSON.parse(JSON.stringify(row[column.property]));
      this.editTarget = target;
      const header = this.$refs.tableRef.querySelector(
        '.el-table__header-wrapper'
      );

      const tableScroll =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.el-table__body-wrapper');

      tableScroll.addEventListener('scroll', this.editCellScrollListener);
      this.editTemplate = column.renderEditCell;
      this.editPosition = {
        x: target.offsetLeft - tableScroll.scrollLeft,
        y: target.offsetTop + header.offsetHeight,
        originLeft: target.offsetLeft,
        width: target.offsetWidth,
        originWidth: target.offsetWidth,
        height: target.offsetHeight
      };
      this.editDisplay = true;
      this.editProp = this.editPrefix + column.property;
      this.editRow = JSON.parse(JSON.stringify(row));
      this.editCellId = rt.newUUID();
      scrollIntoView(target);
      this.$nextTick(() => {
        this.$refs.editCell.$el.style.width = `${this.editPosition.width}px`;
        this.$refs.editCell.$el.style.height = `${this.editPosition.height}px`;
        this.$refs.editCell.$el.style.transform = `translate(${this.editPosition
          .x + 1}px, ${this.editPosition.y + 1}px)`;
        this.$refs.tableRef.querySelector(
          '.rt-table-cell-editor-form'
        ).style.width = `${this.editPosition.width}px`;
        this.$refs.tableRef.querySelector(
          '.rt-table-cell-editor-form'
        ).style.height = `${this.editPosition.height}px`;
        this.$refs.editCell.$el.setAttribute(
          'data-edit-cell-key',
          `${cellKeyValue}-${column.property}`
        );
      });
    },

    editCellScrollListener (e) {
      const tableEl =
        this.$refs.tableRef &&
        this.$refs.tableRef
          .querySelector('.el-table__body-wrapper')
          .getBoundingClientRect();
      this.editPosition.x = this.editPosition.originLeft - e.target.scrollLeft;
      this.$refs.editCell.$el.style.transform = `translate(${this.editPosition.x}px, ${this.editPosition.y}px)`;

      this.$refs.editCell.closeValidate();

      if (this.editPosition.x < 0) {
        // 可编辑单元格左侧溢出样式处理，容器宽度减小，容器位移停在0，内部表单位移
        this.$refs.editCell.$el.style.width =
          this.editPosition.originWidth + this.editPosition.x + 'px';
        this.$refs.editCell.$el.style.transform = `translate(0, ${this.editPosition.y}px)`;
        this.$refs.tableRef.querySelector(
          '.rt-table-cell-editor-form'
        ).style.transform = `translateX(${this.editPosition.x}px)`;
      } else if (
        this.editPosition.x >
        tableEl.width - this.editPosition.originWidth
      ) {
        const drift =
          tableEl.width - this.editPosition.originWidth - this.editPosition.x;
        this.$refs.editCell.$el.style.width =
          this.editPosition.originWidth + drift + 'px';
      } else {
        this.$refs.editCell.$el.style.width = `${this.editPosition.originWidth}px`;
        this.$refs.tableRef.querySelector(
          '.rt-table-cell-editor-form'
        ).style.transform = 'unset';
      }
    },

    handleUpdate () {
      new Promise(resolve => {
        const done = columnProp => resolve(columnProp);
        const stop = () => resolve(false);
        this.$emit(
          'edit-update',
          {
            prop: this.editProp.replace(this.editPrefix, ''),
            value: this.editRow[this.editProp],
            row: this.editRow
          },
          done,
          stop
        );
      }).then(columnProp => {
        if (columnProp !== false) {
          this.hideEditCell();
          this.$nextTick(() => {
            columnProp && this.openEditCell(this.editRow, columnProp);
          });
        }
      });
    },
    handleEditEsc () {
      this.hideEditCell();
      this.$emit('edit-esc');
    },

    hideEditCell () {
      this.dataList.forEach(item => {
        Object.keys(item).forEach(n => {
          if (n.startsWith(this.editPrefix)) {
            delete item[n];
          }
        });
      });
      const tableScroll =
        this.$refs.tableRef &&
        this.$refs.tableRef.querySelector('.el-table__body-wrapper');

      tableScroll &&
        tableScroll.removeEventListener('scroll', this.editCellScrollListener);
      this.editDisplay = false;
    },

    openEditCell (row, columnProp, needImmediateValidate = false) {
      this.needImmediateValidate = needImmediateValidate;
      const cellKeyValue = this.getRowKeyValue(row);
      const column = this.$refs.table.columns.find(item => {
        return item.property === columnProp;
      });
      const target = this.$refs.tableRef.querySelector(
        '.rt-edit-cell-' + cellKeyValue + '-' + columnProp
      );
      if (row && column && target) {
        this.handleCellClick(row, column, target);
      }
    },

    changeTableData (row, columnProp, val) {
      this.dataList.some(item => {
        const isEqualsRow = this.equalsRow(item, row);
        if (isEqualsRow) {
          this.$set(item, columnProp, val);
        }
        return isEqualsRow;
      });
    },

    appendTableData (data, index = 0) {
      this.dataList.splice(index, 0, data);
    },

    getRowKeyValue (row) {
      let rowKeyValue = row['id'];
      if (this.rowKey) {
        rowKeyValue =
          typeof this.rowKey !== 'function'
            ? row[this.rowKey]
            : this.rowKey(row);
      }
      return rowKeyValue;
    }
  }
};
</script>



// WEBPACK FOOTER //
// rtTable.vue?4b1a7202